import { useState, useEffect } from 'react'
import Link from 'next/link'
import { isEmpty, isEmail } from 'lib/utils/validator'
import { getMessageTemplate, isValidMessage } from 'lib/services/inquiry'
import { sendOfferRequests } from 'lib/services/offer-requests'
import LoadingSpinner from 'components/LoadingSpinner'
import { signIn, useSession } from 'next-auth/client'
import { setUserInfo } from 'lib/services/store'
import { useRouter } from 'next/router'
import styles from './SendRequests.module.scss'

export default function SendRequests(props) {
  const {
    formData,
    items,
    triggerSuccess,
    sessionRequired = false,
    children,
    singleOffer,
  } = props
  const [session] = useSession({ required: sessionRequired })
  const [isLoading, setIsLoading] = useState(false)
  const [isValid, setIsValid] = useState(true)
  const [sentPressed, setSentPressed] = useState(false)
  const [errors, setErrors] = useState([])
  const [clientInCart, setClientInCart] = useState(false)
  const LIMIT = 20

  const router = useRouter()

  /**
   * Checks if amount of requests is within limits.
   * Currenly just compares raw number,
   * but we expect this to be expanded to
   * include types of items in next iteration.
   */
  const hasTooManyItems = () => items.length > LIMIT

  /**
   * Check we have fields needed for sending.
   */
  const hasRequiredFields = () => {
    const validationErrors = []

    // Form data.
    const hasEmail = isEmail(formData.email.value)
    const hasName = !isEmpty(formData.name.value)
    const isGuestEmail = formData.email.value === 'vieras@happens.fi'
    const isGuestName = formData.name.value === 'Vieras'

    // Cart item data.
    const isMissingMessage = items.some(
      ({ message }) => !isValidMessage(message, false),
    )
    const isMessageTemplate = items.some(
      ({ message }) => message === getMessageTemplate(),
    )
    const isMissingGuests = items.some(({ guests }) => !guests || guests < 1)
    const tooManyItems = hasTooManyItems()

    if (!hasName) {
      validationErrors.push('Nimi on pakollinen kenttä')
    }

    if (!hasEmail) {
      validationErrors.push('Sähköpostiosoite on pakollinen kenttä')
    }

    if (isMissingMessage) {
      validationErrors.push('Viesti on pakollinen kenttä')
    }

    if (isMessageTemplate) {
      validationErrors.push('Täytäthän viestipohjan')
    }

    if (isMissingGuests) {
      validationErrors.push('Tarkennathan vieraiden määrän')
    }

    if (tooManyItems) {
      validationErrors.push(
        `Korissa on liian monta tarjouspyyntöä (max. ${LIMIT} kpl).`,
      )
    }

    if (isGuestEmail) {
      validationErrors.push(
        'Vaihdathan vierassähköpostin omaan sähköpostiosoitteeseesi',
      )
    }

    if (isGuestName) {
      validationErrors.push('Lisääthän nimesi tilaukseen? Havaittu "vieras"')
    }

    setErrors(validationErrors)

    return validationErrors.length === 0
  }

  /**
   * Check if we have all fields required for send.
   */
  useEffect(() => {
    if (!sentPressed) {
      setIsValid(true)
    } else if (hasRequiredFields()) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [formData, items])
  /**
   * Gather up
   * - Form data
   * - Inquiry data
   * - Offer data
   *
   * And pass on to API endpoint for processing.
   */
  const submitRequest = async () => {
    const validForm = hasRequiredFields()
    setSentPressed(true)
    if (validForm) {
      setIsValid(validForm)
      setIsLoading(true)
      const sendData = { ...formData }
      if (!sessionRequired)
        sendData.login_email = { value: formData.email.value }
      const result = await sendOfferRequests(sendData, singleOffer)
      const responseErrors = new Set()

      result.forEach((offerRequest) => {
        if (offerRequest.errors.length > 0) {
          offerRequest.errors.forEach((error) => responseErrors.add(error))
        }
      })

      setErrors(Array.from(responseErrors))
      setIsLoading(false)

      if (responseErrors.size === 0) {
        triggerSuccess(result)
      }
    } else {
      setIsValid(validForm)
    }
  }

  const handleChangeNewsLetter = () => {
    const value = !formData.newsletter.value
    formData.newsletter.set(value)
    setUserInfo('newsletter', JSON.stringify(value))
  }

  const isClientInCart = () => {
    if (router.asPath.includes('tarjouskori')) {
      setClientInCart(true)
    } else setClientInCart(false)
  }

  useEffect(() => {
    isClientInCart()
  }, [router])
  return (
    <div className={`container ${styles.section}`}>
      {!session && sessionRequired && (
        <button className="btn btn--primary" onClick={() => signIn()}>
          Jatka kirjautumalla sisään
        </button>
      )}

      {(session || !sessionRequired) && (
        <>
          {errors.map((error, index) => (
            <p className={`error ${styles.errorMsg}`} key={index}>
              {error}
            </p>
          ))}
          {clientInCart ? (
            <>
              <div
                className={`row flex end mobile-center ${styles.itemsCenter}`}
              >
                <input
                  id="newsletter"
                  name="newsletter"
                  type="checkbox"
                  className={styles.checkbox}
                  checked={formData.newsletter.value}
                  onChange={handleChangeNewsLetter}
                />
                <label
                  htmlFor="newsletter"
                  className={`flex start ${styles.labelText}`}
                >
                  Uutuuksia ja ideoita! Tilaa uutiskirjeemme.
                </label>
              </div>
              <div className="flex stretch row end mobile-center">
                {isValid && (
                  <button
                    className="btn btn--primary w-100-mobile me-2"
                    onClick={submitRequest}
                  >
                    Lähetä tarjouspyyntö
                  </button>
                )}
                {!isValid && (
                  <button
                    disabled
                    className="btn btn--primary w-100-mobile me-2"
                  >
                    Lähetä tarjouspyyntö
                  </button>
                )}
                {children}
              </div>
              <div className="row streatch flex end mobile-center">
                <p className={styles.terms}>
                  Lähettämällä tarjouspyynnön hyväksyt
                  <Link href="/happensin-kayttoehdot-ja-tietosuojalauseke">
                    <a target="_blank">Käyttöehdot ja Tietosuojalausekkeen</a>
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="row flex mb-3 mobile-center">
                <input
                  id="newsletter"
                  name="newsletter"
                  type="checkbox"
                  className={styles.checkbox}
                  checked={formData.newsletter.value}
                  onChange={handleChangeNewsLetter}
                />
                <label
                  htmlFor="newsletter"
                  className={`flex start ${styles.labelText}`}
                >
                  Uutuuksia ja ideoita! Tilaa uutiskirjeemme.
                </label>
              </div>
              <div className="flex stretch center row mobile-center">
                {isValid && (
                  <button
                    className="btn btn--primary w-100-mobile me-2"
                    onClick={submitRequest}
                  >
                    Lähetä tarjouspyyntö
                  </button>
                )}
                {!isValid && (
                  <button
                    disabled
                    className="btn btn--primary w-100-mobile center me-2"
                  >
                    Lähetä tarjouspyyntö
                  </button>
                )}
                {children}
              </div>
              <div className="flex stretch center row mobile-center">
                <p className={styles.terms}>
                  Lähettämällä tarjouspyynnön hyväksyt
                  <Link href="/happensin-kayttoehdot-ja-tietosuojalauseke">
                    <a target="_blank">Käyttöehdot ja Tietosuojalausekkeen</a>
                  </Link>
                </p>
              </div>
            </>
          )}
          {isLoading && <LoadingSpinner />}
        </>
      )}
    </div>
  )
}
